import { useEffect, useState } from 'react';
import { listRecentSubmissions } from '../lib/api';
import type { PublicSubmission } from '../shared/types';

/**
 * Homepage teaser for the wall: a handful of recent faces, not the full
 * paginated grid. Most visitors never scroll to the wall, so paying for a
 * Lambda call plus photo egress on every homepage load is wasted; the full,
 * "Load more" wall now lives on its own opt-in page at /wall (see Wall.tsx).
 *
 * The public API has no count endpoint, so this deliberately never claims a
 * total -- it shows "recent" framing and up to TEASER_SIZE faces, nothing
 * more specific.
 */
export function WallTeaser() {
  const [items, setItems] = useState<PublicSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    listRecentSubmissions()
      .then((recent) => {
        if (!cancelled) setItems(recent);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the wall right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="section" id="wall" aria-labelledby="wall-heading">
      <div className="section__inner">
        <h2 id="wall-heading" className="section__title">
          Recent people who did it
        </h2>

        {error && <p className="notice notice--error">{error}</p>}

        {!error && !loading && items.length === 0 && (
          <p className="notice">Nobody yet. Be the first.</p>
        )}

        {items.length > 0 && (
          <ul className="wall wall--teaser">
            {items.map((item) => (
              <li key={item.id} className="card">
                {item.photoUrl && (
                  <img
                    className="card__photo"
                    src={item.photoUrl}
                    alt={`${item.name} deleting their DoorDash account`}
                    loading="lazy"
                  />
                )}
                <div className="card__body">
                  <p className="card__name">{item.name}</p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {loading && <p className="notice">Loading…</p>}

        <div className="wall-teaser__cta">
          <a className="btn btn--ghost" href="/wall">
            See the whole wall
          </a>
        </div>
      </div>
    </section>
  );
}
